import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { affiliationAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { CheckCircle2, MapPin } from 'lucide-react';
import ProtectedResumeLink from '../components/afiliacion/ProtectedResumeLink';

const SAFE_PARAM = /^[A-Za-z0-9_.-]{1,64}$/;

const AfiliacionSolicitudPage = () => {
  const [searchParams] = useSearchParams();
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({
    applicant_type: 'individual', full_name: '', email: '', phone: '',
    company_name: '', state: '', section_id_requested: '', exception_reason: '', consent: false,
    source: 'web',
  });
  const [sectionQuery, setSectionQuery] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    affiliationAPI.publicSections().then((secs) => {
      setSections(secs);
      // Preselección validada de sección vía parámetro permitido ?seccion=
      const seccion = searchParams.get('seccion');
      if (seccion && SAFE_PARAM.test(seccion) && secs.some((s) => s.id === seccion)) {
        setForm((f) => ({ ...f, section_id_requested: seccion }));
      }
    }).catch(() => setSections([]));
    // Fuente/campaña sólo por parámetros permitidos y validados
    const fuente = searchParams.get('fuente');
    const campania = searchParams.get('campania');
    if (fuente && SAFE_PARAM.test(fuente)) {
      setForm((f) => ({ ...f, source: campania && SAFE_PARAM.test(campania) ? `${fuente}:${campania}` : fuente }));
    }
  }, [searchParams]);

  const norm = (v) => (v || '').trim().toLowerCase();
  const visibleSections = sections.filter((s) => {
    const q = norm(sectionQuery);
    return !q || [s.name, s.abbreviation, s.state].some((v) => norm(v).includes(q));
  });
  // Recomendación por domicilio: sugerida, NUNCA impuesta.
  const recommended = form.state
    ? sections.find((s) => s.state && norm(s.state) === norm(form.state) && s.id !== form.section_id_requested)
    : null;
  const selectedSection = sections.find((s) => s.id === form.section_id_requested);
  const mismatch = selectedSection && form.state && selectedSection.state &&
    form.state.trim().toLowerCase() !== selectedSection.state.trim().toLowerCase();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError('');
    if (!form.consent) { setError('Debes aceptar el aviso de privacidad.'); return; }
    setSubmitting(true);
    try {
      setResult(await affiliationAPI.createRequest(form));
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  };

  if (result) {
    const resumeUrl = result.resume_url || `${window.location.origin}/afiliacion/retomar/${result.resume_token}`;
    const isDev = (result.env || 'development') !== 'production';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div data-testid="aff-success" className="max-w-lg w-full bg-white rounded-2xl border shadow-sm p-8">
          <CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">¡Solicitud recibida!</h1>
          <p className="text-gray-600 mt-2">Tu folio de afiliación:</p>
          <p data-testid="aff-folio" className="text-2xl font-mono font-bold text-gray-900 my-2">{result.folio}</p>
          {result.needs_section_review && (
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm mt-3">
              Tu asignación de sección quedó marcada para revisión.
            </p>
          )}
          {result.notice && <p className="text-blue-700 bg-blue-50 rounded-lg p-3 text-sm mt-3">{result.notice}</p>}
          <p className="text-sm text-gray-500 mt-4">
            {isDev ? 'Notificación de prueba registrada (DEV). Enlace para retomar:' : 'Guarda este enlace para continuar más tarde:'}
          </p>
          {isDev && <span data-testid="aff-env-badge" className="inline-block text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded mb-1">ENTORNO DEV</span>}
          <div className="mt-2">
            <ProtectedResumeLink url={resumeUrl} isProduction={!isDev} testid="aff-resume-link" />
          </div>
          <div className="mt-6 border-t pt-5">
            <p className="text-sm text-gray-700">
              Cuota anual de afiliación: <strong>$3,500 MXN</strong>
            </p>
            <button
              type="button"
              data-testid="aff-pagar-btn"
              onClick={async () => {
                try {
                  const r = await affiliationAPI.crearCheckout(result.folio);
                  window.location.href = r.checkout_url;
                } catch (e) {
                  alert(e.message || 'No se pudo iniciar el pago');
                }
              }}
              className="mt-3 w-full bg-gray-900 text-white rounded-md py-3 font-semibold hover:bg-gray-800"
            >
              Pagar afiliación (entorno de pruebas)
            </button>
            <p className="text-xs text-gray-500 mt-2">
              El pago se confirma únicamente por el webhook de Stripe; tu sección revisará el
              expediente para completar la afiliación.
            </p>
          </div>
          {isDev && <p className="text-[11px] text-gray-400 mt-2">DEV: la notificación real (correo/WhatsApp) aún no se envía.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border shadow-sm p-8">
        <h1 className="text-3xl font-bold text-gray-900">Afíliate a UPIM</h1>
        <p className="text-gray-500 mt-1">Completa tu solicitud y elige tu sección territorial.</p>

        {error && <div data-testid="aff-error" className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

        <div className="mt-6 space-y-5">
          <div className="flex gap-3">
            {['individual', 'inmobiliaria'].map((t) => (
              <button key={t} data-testid={`type-${t}`} onClick={() => set('applicant_type', t)}
                className={`flex-1 border rounded-xl px-4 py-3 text-sm font-medium capitalize transition-colors ${form.applicant_type === t ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600'}`}>
                {t === 'individual' ? 'Persona individual' : 'Inmobiliaria (persona moral)'}
              </button>
            ))}
          </div>

          <input data-testid="aff-name" placeholder="Nombre completo" value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className="w-full border rounded-lg px-4 py-2.5" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input data-testid="aff-email" type="email" placeholder="Correo electrónico" value={form.email} onChange={(e) => set('email', e.target.value)} className="w-full border rounded-lg px-4 py-2.5" />
            <input data-testid="aff-phone" placeholder="Teléfono" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="w-full border rounded-lg px-4 py-2.5" />
          </div>
          {form.applicant_type === 'inmobiliaria' && (
            <input data-testid="aff-company" placeholder="Razón social / Nombre de la inmobiliaria" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className="w-full border rounded-lg px-4 py-2.5" />
          )}
          <input data-testid="aff-state" placeholder="Estado (domicilio)" value={form.state} onChange={(e) => set('state', e.target.value)} className="w-full border rounded-lg px-4 py-2.5" />

          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1"><MapPin className="h-4 w-4" /> ¿A qué sección de UPIM deseas pertenecer? *</label>
            {recommended && (
              <div data-testid="aff-section-recommendation" className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center justify-between gap-3">
                <span>Por tu domicilio te sugerimos <strong>{recommended.name}</strong>. Puedes elegir otra sección.</span>
                <button type="button" data-testid="aff-use-recommended" onClick={() => set('section_id_requested', recommended.id)}
                  className="shrink-0 border border-blue-300 rounded-md px-3 py-1 text-xs font-semibold hover:bg-blue-100">Usar esta</button>
              </div>
            )}
            <input data-testid="aff-section-search" placeholder="Buscar sección por nombre, siglas o estado…" value={sectionQuery}
              onChange={(e) => setSectionQuery(e.target.value)} className="w-full border rounded-lg px-4 py-2.5 mt-2" />
            <select data-testid="aff-section" value={form.section_id_requested} onChange={(e) => set('section_id_requested', e.target.value)} className="w-full border rounded-lg px-4 py-2.5 mt-2 bg-white">
              <option value="">Selecciona una sección…</option>
              {visibleSections.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.abbreviation}){s.state ? ` — ${s.state}` : ''}</option>)}
            </select>
            {sections.length > 0 && visibleSections.length === 0 && (
              <p data-testid="aff-section-empty" className="text-xs text-gray-500 mt-1">Ninguna sección coincide con tu búsqueda.</p>
            )}
            {sections.length === 0 && <p className="text-xs text-amber-600 mt-1">No hay secciones activas; tu solicitud irá a UPIM Nacional / Pendiente.</p>}
          </div>

          {mismatch && (
            <div>
              <label className="text-sm text-amber-700">Elegiste una sección distinta a tu domicilio. Indica el motivo:</label>
              <input data-testid="aff-reason" placeholder="Motivo de la excepción" value={form.exception_reason} onChange={(e) => set('exception_reason', e.target.value)} className="w-full border rounded-lg px-4 py-2.5 mt-1" />
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-gray-600">
            <input data-testid="aff-consent" type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} className="mt-1" />
            Acepto el aviso de privacidad y el tratamiento de mis datos conforme a la política de UPIM.
          </label>

          <Button data-testid="aff-submit" onClick={submit} disabled={submitting} className="w-full py-6 text-base">
            {submitting ? 'Enviando…' : 'Enviar solicitud'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AfiliacionSolicitudPage;
